import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useLayout } from "@/../_metronic/layout/core";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import { TbReportSearch } from "react-icons/tb";
import { FaEye } from "react-icons/fa";
import Button from "react-bootstrap/Button";

import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { SHORT_MONTHS } from "@/utils/enums/DateEnum";
import { User } from "@/@types/sintegrada/User";
import {
	getRiskClassificationDashboard,
	RiskClassificationDashboardData,
} from "@/services/sintegrada/attendance/getRiskClassificationDashboard";
import { getProfessionals } from "@/services/sintegrada/professional/getProfessionals";
import { timeToString } from "@/utils/dateUtil";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { RiskClassificationDashboardFilters } from "./RiskClassificationDashboardFilters";
import { BarChart } from "@/components/Charts/BarChart";
import { DonutChart } from "@/components/Charts/DonutChart";
import { RiskClassificationDashboardCards } from "./RiskClassificationDashboardCards";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PrintComponent } from "@/components/PrintComponent";
import { AverageDurationOfAttandanceTable } from "./AverageDurationOfAttendanceTable";
import { ClassificationByUbsTable } from "./ClassificationByUbsTable";
import { AverageWaitingForRisckClassificationTable } from "./AverageWaitingTimeForRiskClassificationTable";
import AttendanceByProfessionalTable from "./AttendanceByProfessionalTable";
import { ClassificationByUbsModal } from "./ClassificationByUbsModal";
import { AttendanceByProfessionalModal } from "./AttendanceByProfessionalModal";
import { TimeDetailsModal } from "./TimeDetailsModal";
import { AttendanceConditions } from "./AttendanceConditions";

dayjs.extend(utc);
dayjs.extend(timezone);

export function RiskClassificationDashboard() {
	const { uf, ibgeCode, cnes } = useCurrentAccount();
	const { setTitle } = useLayout();
	const [startDate, setStartDate] = useState(
		dayjs().add(-1, "month").startOf("day").format("YYYY-MM-DDTHH:mm")
	);
	const [endDate, setEndDate] = useState(dayjs().endOf("day").format("YYYY-MM-DDTHH:mm"));
	const [minAge, setMinAge] = useState("");
	const [maxAge, setMaxAge] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [professionalId, setProfessionalId] = useState<string>();
	const [professionals, setProfessionals] = useState<User[]>([]);
	const [dashboard, setDashboard] = useState<RiskClassificationDashboardData>();
	const printPageRef = useRef<HTMLDivElement | null>(null);
	const [showAttendanceByProfessionalModal, setShowAttendanceByProfessionalModal] =
		useState(false);
	const [showClassificationByUbsModal, setShowClassificationByUbsModal] = useState(false);
	const [showTimeDetailsModal, setShowTimeDetailsModal] = useState(false);

	const printPage = useReactToPrint({
		content: () => printPageRef.current,
	});

	const dashboardData = dashboard || {
		aggregatedByGender: [],
		aggregatedByHealthUnitAndRiskClassification: [],
		aggregatedByPeriodAndRiskClassificationStatus: [],
		averageTimeInSecondsByRiskClassification: [],
		averageWaitTimeInSecondsByRiskClassification: [],
		aggregatedByRiskClassification: [],
		aggregatedByAttendanceConditions: [],
		aggregatedByProfessionalsAndRiskClassificationStatus: [],
		averageTimeInSecondsByProfessional: [],
		averageTimeInSeconds: 0,
		averageWaitTimeInSeconds: 0,
		maxTimeInSeconds: 0,
		minTimeInSeconds: 0,
		maxWaitTimeInSeconds: 0,
		minWaitTimeInSeconds: 0,
		maxTimeAttendanceDetails: {
			attendance_id: 0,
			patient_name: "",
			patient_cpf: "",
			attendance_date: "",
			risk_classification_name: "",
			risk_classification_color: "",
		},
		minTimeAttendanceDetails: {
			attendance_id: 0,
			patient_name: "",
			patient_cpf: "",
			attendance_date: "",
			risk_classification_name: "",
			risk_classification_color: "",
		},
		maxWaitTimeAttendanceDetails: {
			attendance_id: 0,
			patient_name: "",
			patient_cpf: "",
			attendance_date: "",
			risk_classification_name: "",
			risk_classification_color: "",
		},
		minWaitTimeAttendanceDetails: {
			attendance_id: 0,
			patient_name: "",
			patient_cpf: "",
			attendance_date: "",
			risk_classification_name: "",
			risk_classification_color: "",
		},
		evasions: 0,
		totalAttendances: 0,
		totalAttendancesClassifiedMoreThanOnce: 0,
		totalAttendancesClassifiedOnce: 0,
		totalAttendancesUnclassified: 0,
	};

	async function fetchDashboard() {
		setIsLoading(true);
		setDashboard(
			await getRiskClassificationDashboard({
				uf,
				ibgeCode,
				cnes,
				startDate: dayjs(startDate)
					.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
					.startOf("minute")
					.format("YYYY-MM-DDTHH:mm:ssZ"),
				endDate: dayjs(endDate)
					.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
					.endOf("minute")
					.format("YYYY-MM-DDTHH:mm:ssZ"),
				professionalId: professionalId ? Number(professionalId) : undefined,
				minAge: Number(minAge),
				maxAge: Number(maxAge) || null,
			})
		);
		setIsLoading(false);
	}

	async function fetchProfessional() {
		setProfessionals(await getProfessionals({ uf, ibgeCode, cnes }));
	}

	useEffect(() => {
		setTitle("Monitoramento - Classificação de risco");
		fetchDashboard();
		fetchProfessional();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="p-2">
			<LoadingScreen loading={isLoading} />
			<Row className="mx-1">
				<Card className="h-100 shadow-sm">
					<Card.Body>
						<RiskClassificationDashboardFilters
							startDate={startDate}
							setStartDate={setStartDate}
							endDate={endDate}
							setEndDate={setEndDate}
							minAge={minAge}
							setMinAge={setMinAge}
							maxAge={maxAge}
							setMaxAge={setMaxAge}
							setProfessionalId={setProfessionalId}
							professionals={professionals}
							handleFilter={fetchDashboard}
							handlePrint={printPage}
						/>
					</Card.Body>
				</Card>
			</Row>
			<Row className="mx-1 mt-3">
				<Card className="bg-white shadow-sm">
					<Card.Header className="p-0">
						<Card.Title className="d-flex align-items-center gap-2 p-2 text-primary mb-0">
							<TbReportSearch
								size={30}
								className="bg-primary text-white rounded p-1 me-2"
							/>
							Classificação de risco
						</Card.Title>
					</Card.Header>
				</Card>
			</Row>
			<PrintComponent
				printComponentRef={printPageRef}
				headerContainer={
					<div>
						<p className="text-center fs-3">
							Relatório do monitoramento da classificação de risco
						</p>
						{startDate && endDate && (
							<p>
								<strong>Período:</strong>
								<span>{` ${startDate} á ${endDate} `}</span>
							</p>
						)}
						{professionalId && (
							<p>
								<strong>Profissional:</strong>
								<span>
									{
										professionals.find((p) => p.id === Number(professionalId))
											?.name
									}
								</span>
							</p>
						)}
					</div>
				}
			>
				<Row className="ms-3 mt-3">
					<RiskClassificationDashboardCards
						totalAttendances={dashboardData.totalAttendances}
						totalAttendancesClassificated={
							dashboardData.totalAttendancesClassifiedOnce +
							dashboardData.totalAttendancesClassifiedMoreThanOnce
						}
						totalAttendancesClassifiedOnce={
							dashboardData.totalAttendancesClassifiedOnce
						}
						totalAttendancesClassifiedMoreThanOnce={
							dashboardData.totalAttendancesClassifiedMoreThanOnce
						}
						totalAttendancesUnclassified={dashboardData.totalAttendancesUnclassified}
						totalEvasions={dashboardData.evasions}
						percentageEvasions={calculatePercentage(
							dashboardData.evasions,
							dashboardData.totalAttendancesClassifiedOnce +
								dashboardData.totalAttendancesClassifiedMoreThanOnce +
								dashboardData.evasions
						)}
					/>
				</Row>

				{/* Métricas de Tempo - Classificação de Risco */}
				<Row className="p-2">
					<Col sm={12} className="mb-3">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex justify-content-between fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Métricas de Tempo - Classificação de Risco</Card.Title>
								<Button
									className="gap-2 no-print"
									variant="outline-primary"
									size="sm"
									onClick={() => setShowTimeDetailsModal(true)}
								>
									<FaEye /> Detalhes
								</Button>
							</Card.Header>
							<Card.Body>
								<Row>
									<Col md={6} className="mb-3">
										<div className="text-center">
											<h5 className="text-muted mb-2">
												Tempo de Atendimento
											</h5>
											<div className="row">
												<Col xs={4} className="text-center">
													<div className="border-end">
														<div
															className="fw-bold text-primary"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(
																dashboardData.averageTimeInSeconds
															)}
														</div>
														<small className="text-muted">Médio</small>
													</div>
												</Col>
												<Col xs={4} className="text-center">
													<div className="border-end">
														<div
															className="fw-bold text-success"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(
																dashboardData.minTimeInSeconds
															)}
														</div>
														<small className="text-muted">Mínimo</small>
													</div>
												</Col>
												<Col xs={4} className="text-center">
													<div>
														<div
															className="fw-bold text-danger"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(
																dashboardData.maxTimeInSeconds
															)}
														</div>
														<small className="text-muted">Máximo</small>
													</div>
												</Col>
											</div>
										</div>
									</Col>
									<Col md={6} className="mb-3">
										<div className="text-center">
											<h5 className="text-muted mb-2">Tempo de Espera</h5>
											<div className="row">
												<Col xs={4} className="text-center">
													<div className="border-end">
														<div
															className="fw-bold text-primary"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(
																dashboardData.averageWaitTimeInSeconds
															)}
														</div>
														<small className="text-muted">Médio</small>
													</div>
												</Col>
												<Col xs={4} className="text-center">
													<div className="border-end">
														<div
															className="fw-bold text-success"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(
																dashboardData.minWaitTimeInSeconds
															)}
														</div>
														<small className="text-muted">Mínimo</small>
													</div>
												</Col>
												<Col xs={4} className="text-center">
													<div>
														<div
															className="fw-bold text-danger"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(
																dashboardData.maxWaitTimeInSeconds
															)}
														</div>
														<small className="text-muted">Máximo</small>
													</div>
												</Col>
											</div>
										</div>
									</Col>
								</Row>
							</Card.Body>
						</Card>
					</Col>
				</Row>
				<Row className="p-2 page-break-before">
					<Col sm={12} lg={12} className="mb-2">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex flex-column fs-2 fw-bolder justify-content-start align-items-start">
								<Card.Title className="pt-2">
									Atendimentos classificados por período
								</Card.Title>
								<p className="text-muted">
									Não são consideradas as reclassificações
								</p>
							</Card.Header>
							<Card.Body className="d-flex justify-content-center align-items-center">
								<BarChart
									categories={dashboardData.aggregatedByPeriodAndRiskClassificationStatus.map(
										(aggregated) =>
											`${aggregated.key.year}/${
												SHORT_MONTHS[aggregated.key.month - 1]
											}`
									)}
									series={[
										{
											name: "Atendimentos classificados",
											data: dashboardData.aggregatedByPeriodAndRiskClassificationStatus.map(
												(aggregated) =>
													(aggregated.values || [])
														.filter(
															(riskClassificationGroup) =>
																riskClassificationGroup.key
														)
														.map(
															(riskClassificationGroup) =>
																riskClassificationGroup.total
														)
														.reduce((acc, curr) => acc + curr, 0)
											),
										},
										{
											name: "Atendimentos",
											data: dashboardData.aggregatedByPeriodAndRiskClassificationStatus.map(
												(aggregated) => aggregated.total
											),
										},
									]}
									minHeight={400}
									baseMultiplier={10}
									categoryTextWidth={400}
									isVertical
								/>
							</Card.Body>
						</Card>
					</Col>
				</Row>
				<Row className="p-2 page-break-before">
					<Col sm={12} lg={6} className="mb-2">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Atendimentos por classificação de risco</Card.Title>
							</Card.Header>
							<Card.Body className="d-flex justify-content-center align-items-center">
								<DonutChart
									labels={dashboardData.aggregatedByRiskClassification.map(
										(aggregated) => aggregated.key?.name || "Não informado"
									)}
									series={dashboardData.aggregatedByRiskClassification.map(
										(aggregated) => aggregated.total
									)}
									colors={dashboardData.aggregatedByRiskClassification.map(
										(aggregated) => aggregated.key?.color || "#6c757d"
									)}
								/>
							</Card.Body>
						</Card>
					</Col>
					<Col sm={12} lg={6} className="mb-2">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Atendimentos de classificação por gênero</Card.Title>
							</Card.Header>
							<Card.Body className="d-flex justify-content-center align-items-center">
								<DonutChart
									labels={dashboardData.aggregatedByGender.map((aggregated) =>
										aggregated.key === "M"
											? "Masculino"
											: aggregated.key === "F"
											? "Feminino"
											: "Outros"
									)}
									series={dashboardData.aggregatedByGender.map(
										(aggregated) => aggregated.total
									)}
									colors={dashboardData.aggregatedByGender.map((aggregated) =>
										aggregated.key === "M"
											? "#007bff"
											: aggregated.key === "F"
											? "#e83e8c"
											: "#6c757d"
									)}
								/>
							</Card.Body>
						</Card>
					</Col>
				</Row>

				{/* Condições de Atendimento */}
				<Row className="p-2">
					<Col sm={12} lg={12} className="mb-3">
						<AttendanceConditions
							aggregatedByAttendanceConditions={
								dashboardData.aggregatedByAttendanceConditions
							}
						/>
					</Col>
				</Row>

				<ClassificationByUbsModal
					show={showClassificationByUbsModal}
					onHide={() => setShowClassificationByUbsModal(false)}
					aggregatedByHealthUnitAndRiskClassification={
						dashboardData.aggregatedByHealthUnitAndRiskClassification
					}
				/>
				<Row className="p-2 page-break-before">
					<Col sm={12} lg={8} className="mb-2">
						<Card className="h-100 shadow-sm pb-4">
							<Card.Header className="d-flex justify-content-between fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Atendimentos por classificação e UBS</Card.Title>
								<Button
									className="gap-2 no-print"
									variant="secondary"
									onClick={() => setShowClassificationByUbsModal(true)}
								>
									<FaEye /> Detalhes
								</Button>
							</Card.Header>
							<Card.Body className="overflow-y-scroll" style={{ maxHeight: "500px" }}>
								<ClassificationByUbsTable
									aggregatedByHealthUnitAndRiskClassification={dashboardData.aggregatedByHealthUnitAndRiskClassification.slice(
										0,
										10
									)}
								/>
							</Card.Body>
						</Card>
					</Col>
					<Col md={12} lg={4} className="mb-2">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title className="text-center p-0">
									Tempo médio de espera para classificação de risco
								</Card.Title>
							</Card.Header>
							<Card.Body className="overflow-y-scroll" style={{ maxHeight: "500px" }}>
								<AverageWaitingForRisckClassificationTable
									aggregatedByRiskClassification={
										dashboardData.aggregatedByRiskClassification
									}
									averageWaitTimeInSecondsByRiskClassification={
										dashboardData.averageWaitTimeInSecondsByRiskClassification
									}
								/>
							</Card.Body>
						</Card>
					</Col>
				</Row>
				<AttendanceByProfessionalModal
					show={showAttendanceByProfessionalModal}
					onHide={() => setShowAttendanceByProfessionalModal(false)}
					aggregatedByProfessionalsAndRiskClassificationStatus={
						dashboardData.aggregatedByProfessionalsAndRiskClassificationStatus
					}
					averageTimeInSecondsByProfessional={
						dashboardData.averageTimeInSecondsByProfessional
					}
				/>
				<Row className="p-2 page-break-before">
					<Col sm={12} lg={8} className="mb-2">
						<Card className="h-100 shadow-sm pb-4">
							<Card.Header className="d-flex justify-content-between fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Classificações por profissional</Card.Title>
								<Button
									className="gap-2 no-print"
									variant="secondary"
									onClick={() => setShowAttendanceByProfessionalModal(true)}
								>
									<FaEye /> Detalhes
								</Button>
							</Card.Header>
							<Card.Body className="overflow-y-scroll" style={{ maxHeight: "500px" }}>
								<AttendanceByProfessionalTable
									aggregatedByProfessionalsAndRiskClassificationStatus={dashboardData.aggregatedByProfessionalsAndRiskClassificationStatus.slice(
										0,
										10
									)}
									averageTimeInSecondsByProfessional={
										dashboardData.averageTimeInSecondsByProfessional
									}
								/>
							</Card.Body>
						</Card>
					</Col>
					<Col md={12} lg={4} className="mb-2">
						<Card className="h-100 shadow-sm pb-4">
							<Card.Header className="d-flex p-0 fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title className="text-center p-0">
									Tempo médio de duração do atendimento
								</Card.Title>
							</Card.Header>
							<Card.Body className="overflow-y-scroll" style={{ maxHeight: "500px" }}>
								<AverageDurationOfAttandanceTable
									averageTimeInSecondsByRiskClassification={
										dashboardData.averageTimeInSecondsByRiskClassification
									}
								/>
							</Card.Body>
						</Card>
					</Col>
				</Row>
				<TimeDetailsModal
					show={showTimeDetailsModal}
					onHide={() => setShowTimeDetailsModal(false)}
					maxTimeAttendanceDetails={dashboardData.maxTimeAttendanceDetails}
					minTimeAttendanceDetails={dashboardData.minTimeAttendanceDetails}
					maxWaitTimeAttendanceDetails={dashboardData.maxWaitTimeAttendanceDetails}
					minWaitTimeAttendanceDetails={dashboardData.minWaitTimeAttendanceDetails}
				/>
			</PrintComponent>
		</div>
	);
}
